import React, { useMemo, useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { QRCodeCanvas } from "qrcode.react";
import { MdCheckCircle, MdQrCodeScanner, MdRefresh, MdClose, MdContentCopy } from "react-icons/md";
import { API_URL } from "../../constant";
import "./RetailTcDistribution.css";

const TOPCOIN_RULE_AMOUNT = 5;

export const SESSION_STORAGE_KEY_EMPLOYEE_NO = "retail_tc_employee_no";
export const SESSION_STORAGE_KEY_EMPLOYEE_NAME = "retail_tc_employee_name";

const getSessionItem = (key) => {
  try {
    return sessionStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
};

const setSessionItem = (key, value) => {
  try {
    if (value !== undefined && value !== null) {
      sessionStorage.setItem(key, value);
    }
  } catch (e) {
    console.error("Failed to write to sessionStorage:", e);
  }
};

export const calculateReward = (transactionTotal, distributionRule = null) => {
  const amount = Number(transactionTotal) || 0;
  if (amount <= 0) {
    return { topcoin: 0, mTopcoin: 0 };
  }

  // If RetailBusiness industry status is inactive, distribute based on /it-portal/topcoin-distribution-value
  if (
    distributionRule &&
    (distributionRule.mode === "topcoin_distribution_value" ||
      (distributionRule.is_industry_active === false && Number(distributionRule.price) > 0))
  ) {
    const price = Number(distributionRule.price) || 5;
    const topcoinRate = Number(distributionRule.topcoin_rate) || 1;
    const miniRate = Number(distributionRule.minitopcoin_rate) || 500;

    const units = Math.floor(amount / price);
    const topcoin = units * topcoinRate;
    const remainder = amount % price;
    const mTopcoin = Math.floor((remainder / price) * miniRate);

    return { topcoin, mTopcoin };
  }

  // Otherwise, distribute based on already defined system rule (£5 = 1 TopCoin)
  const ruleAmount = Number(distributionRule?.rule_amount) || TOPCOIN_RULE_AMOUNT;
  const topcoin = Math.floor(amount / ruleAmount);
  const remainder = amount % ruleAmount;
  const mTopcoin = Math.floor((remainder / ruleAmount) * 50);

  return { topcoin, mTopcoin };
};

function RetailTcDistribution() {
  const outletContext = useOutletContext();
  const userData = outletContext?.userData;
  const token = localStorage.getItem("auth_token");

  const [resolvedRetailBusinessId, setResolvedRetailBusinessId] = useState(null);
  const [isLoadingRetailBusiness, setIsLoadingRetailBusiness] = useState(false);
  const [distributionRule, setDistributionRule] = useState(
    userData?.distribution_rule || null
  );

  // Form Fields: Employee No, Employee Name, Transaction Amount, Invoice Number, and Transaction Type
  const [formData, setFormData] = useState({
    employee_no: getSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NO),
    employee_name: getSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NAME),
    transaction_total: "",
    invoice_no: "",
    transaction_type: "Retail Purchase",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState("form"); // "form" | "qr" | "completed"
  const [distributionData, setDistributionData] = useState(null);
  const [completedTxData, setCompletedTxData] = useState(null);
  const [testCustomerId, setTestCustomerId] = useState("");
  const [isSimulatingClaim, setIsSimulatingClaim] = useState(false);

  const pollIntervalRef = useRef(null);

  // Sync distribution rule from userData whenever it changes
  useEffect(() => {
    if (userData?.distribution_rule) {
      setDistributionRule(userData.distribution_rule);
    }
  }, [userData?.distribution_rule]);

  // Fetch the latest distribution rule on mount to ensure fresh state
  useEffect(() => {
    const fetchDistributionRule = async () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) return;
        const res = await fetch(`${API_URL}/api/retail-business/distribution-rule`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        if (res.ok) {
          const resData = await res.json();
          if (resData?.data) {
            setDistributionRule(resData.data);
          }
        }
      } catch (err) {
        console.error("Error fetching distribution rule:", err);
      }
    };

    fetchDistributionRule();
  }, []);

  // Pre-fetch retail business ID when component mounts
  useEffect(() => {
    const fetchRetailBusinessId = async () => {
      if (userData?.retail_business_id || userData?.id) {
        const id = userData.retail_business_id || userData.id;
        setResolvedRetailBusinessId(id);
        if (userData?.distribution_rule) {
          setDistributionRule(userData.distribution_rule);
        }
        return;
      }

      if (!token) {
        toast.error("Authentication token missing. Please log in again.");
        return;
      }

      if (resolvedRetailBusinessId !== null) return;

      setIsLoadingRetailBusiness(true);
      try {
        const res = await fetch(`${API_URL}/api/retail-business/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (res.ok) {
          const data = await res.json();
          const id =
            data?.user?.retail_business_id ||
            data?.user?.id ||
            data?.retail_business_id ||
            data?.id;
          if (id) {
            setResolvedRetailBusinessId(id);
          }
          if (data?.user?.distribution_rule) {
            setDistributionRule(data.user.distribution_rule);
          }
        }
      } catch (error) {
        console.error("Failed to fetch retail business:", error);
      } finally {
        setIsLoadingRetailBusiness(false);
      }
    };

    fetchRetailBusinessId();
  }, [userData, token]);

  // Clean up polling timer on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Live calculation of TopCoin & M-TopCoin
  const calculatedReward = useMemo(
    () => calculateReward(formData.transaction_total, distributionRule),
    [formData.transaction_total, distributionRule]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "employee_no") {
      setSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NO, value);
    } else if (name === "employee_name") {
      setSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NAME, value);
    }
  };

  // Poll distribution status when in "qr" step
  const startPolling = (distToken) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/retail-business/tc-distribution/status/${encodeURIComponent(distToken)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        if (!res.ok) return;

        const result = await res.json();
        if (result.success && result.data) {
          if (result.data.status === "completed") {
            clearInterval(pollIntervalRef.current);
            setCompletedTxData(result.data);
            setStep("completed");
            toast.success("Transaction completed! Customer scanned the QR code.");
            if (result.data.employee_no) {
              setSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NO, result.data.employee_no);
            }
            if (result.data.employee_name) {
              setSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NAME, result.data.employee_name);
            }
          } else if (result.data.status === "expired" || result.data.status === "cancelled") {
            clearInterval(pollIntervalRef.current);
            toast.info(`Distribution was ${result.data.status}.`);
            setStep("form");
          }
        }
      } catch (err) {
        console.error("Error polling distribution status:", err);
      }
    }, 2500);
  };

  // Click Complete Transaction -> initiate distribution and generate QR
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.employee_no.trim()) {
      toast.error("Please enter Employee No.");
      return;
    }

    if (!formData.employee_name.trim()) {
      toast.error("Please enter Employee Name.");
      return;
    }

    const amount = Number(formData.transaction_total);
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid Transaction Amount greater than 0.");
      return;
    }

    if (!formData.invoice_no.trim()) {
      toast.error("Please enter Invoice Number.");
      return;
    }

    setIsSubmitting(true);

    // Save/update employee details in session on initiating/completing transaction
    setSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NO, formData.employee_no.trim());
    setSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NAME, formData.employee_name.trim());

    try {
      const payload = {
        employee_no: formData.employee_no.trim(),
        employee_name: formData.employee_name.trim(),
        transaction_total: amount,
        invoice_no: formData.invoice_no.trim(),
        transaction_type: formData.transaction_type,
      };

      const res = await fetch(`${API_URL}/api/retail-business/tc-distribution/initiate`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to initiate distribution QR.");
      }

      setDistributionData(data.data);
      setStep("qr");
      toast.success("QR Code generated! Customer can now scan to complete transaction.");

      // Start real-time polling to detect when customer scans and claims
      startPolling(data.data.token);
    } catch (err) {
      console.error("Initiate error:", err);
      toast.error(err.message || "Failed to generate transaction QR.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel the pending QR session
  const handleCancelSession = async () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    if (distributionData?.token) {
      try {
        await fetch(
          `${API_URL}/api/retail-business/tc-distribution/cancel/${encodeURIComponent(distributionData.token)}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
      } catch (err) {
        console.error("Cancel error:", err);
      }
    }

    setDistributionData(null);
    setStep("form");
    toast.info("Distribution session cancelled.");
  };

  // Reset to form to start a new transaction
  const handleNewTransaction = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    setDistributionData(null);
    setCompletedTxData(null);
    setFormData((prev) => ({
      ...prev,
      employee_no: getSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NO) || prev.employee_no,
      employee_name: getSessionItem(SESSION_STORAGE_KEY_EMPLOYEE_NAME) || prev.employee_name,
      transaction_total: "",
      invoice_no: "",
    }));
    setStep("form");
  };

  // Simulation tool for testing customer scanning
  const handleSimulateClaim = async (e) => {
    e.preventDefault();
    if (!distributionData?.token) return;

    setIsSimulatingClaim(true);
    try {
      const res = await fetch(`${API_URL}/api/customer/tc-distribution/claim`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          token: distributionData.token,
          customer_id: testCustomerId ? testCustomerId.trim() : undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Simulation failed");
      }

      toast.success("Claim simulated successfully! Poller will detect completion.");
    } catch (err) {
      toast.error(err.message || "Failed to simulate claim.");
    } finally {
      setIsSimulatingClaim(false);
    }
  };

  const copyClaimUrl = () => {
    if (!distributionData?.qr_data) return;
    navigator.clipboard.writeText(distributionData.qr_data);
    toast.info("Customer Claim Link copied to clipboard!");
  };

  // Format current date & time for header display matching ManualTopCoinWindow
  const now = new Date();
  const displayDate = now.toLocaleDateString("en-GB");
  const displayTime = now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  if (isLoadingRetailBusiness) {
    return (
      <div className="rtcd-outer">
        <div className="rtcd-loading">
          <p>Loading retail business information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rtcd-outer">
      {/* Dark purple decorative border frame */}
      <div className="rtcd-border-frame">
        {/* White receipt card */}
        <div className="rtcd-card">

          {/* ---- Header ---- */}
          <div className="rtcd-header">
            <div className="rtcd-header-left">
              <h3>TopCoin UK<br />(TCUK)</h3>
            </div>
            <div className="rtcd-header-right">
              {displayDate}{" "}{displayTime}
            </div>
          </div>

          {/* ---- Branding row ---- */}
          <div className="rtcd-branding">
            <p className="brand-name">TopCoinLand</p>
            <a
              className="brand-link"
              href="https://www.topcoinland.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.topcoinland.com
            </a>
            <div className="rtcd-page-badge">
              <MdQrCodeScanner /> Retail TC Distribution
            </div>
          </div>

          {/* ========================================================
              STEP 1: Form View (Default)
              ======================================================== */}
          {step === "form" && (
            <form onSubmit={handleSubmit}>
              {/* Employee No */}
              <div className="rtcd-form-row">
                <span className="label-text">Employee No:</span>
                <input
                  className="field-input"
                  name="employee_no"
                  type="text"
                  placeholder="e.g. EMP-101"
                  value={formData.employee_no}
                  onChange={handleChange}
                  required
                  maxLength={100}
                />
              </div>

              {/* Employee Name */}
              <div className="rtcd-form-row">
                <span className="label-text">Employee Name:</span>
                <input
                  className="field-input"
                  name="employee_name"
                  type="text"
                  placeholder="e.g. Sarah Connor"
                  value={formData.employee_name}
                  onChange={handleChange}
                  required
                  maxLength={255}
                />
              </div>

              {/* Transaction Amount */}
              <div className="rtcd-form-row">
                <span className="label-text">Transaction Amount:</span>
                <input
                  className="field-input currency-input"
                  name="transaction_total"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="£0.00"
                  value={formData.transaction_total}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Awarding TopCoin (Auto-Calculated) */}
              <div className="rtcd-award-row">
                <span className="label-text">Awarding TopCoin:</span>
                <span className="award-value">{calculatedReward.topcoin} TC</span>
              </div>

              {/* Awarding M-TopCoin (Auto-Calculated) */}
              <div className="rtcd-award-row">
                <span className="label-text">Awarding M-TopCoin:</span>
                <span className="award-value">{calculatedReward.mTopcoin} mTC</span>
              </div>

              {/* Invoice Number */}
              <div className="rtcd-form-row">
                <span className="label-text">Invoice Number:</span>
                <input
                  className="field-input"
                  name="invoice_no"
                  type="text"
                  placeholder="e.g. INV-10042"
                  value={formData.invoice_no}
                  onChange={handleChange}
                  required
                  maxLength={100}
                />
              </div>

              {/* Transaction Type */}
              <div className="rtcd-form-row">
                <span className="label-text">Transaction Type:</span>
                <select
                  className="field-input"
                  name="transaction_type"
                  value={formData.transaction_type}
                  onChange={handleChange}
                  required
                >
                  <option value="Retail Purchase">Retail Purchase</option>
                  <option value="Collectable Card Purchase">Collectable Card Purchase</option>
                  <option value="Collectable Card Transfer">Collectable Card Transfer</option>
                  <option value="Refund">Refund</option>
                  <option value="Auction">Auction</option>
                </select>
              </div>

              {/* Submit Area */}
              <div className="rtcd-submit-area">
                <button
                  type="submit"
                  className="rtcd-submit-btn"
                  disabled={isSubmitting || !formData.transaction_total || Number(formData.transaction_total) <= 0}
                >
                  <MdQrCodeScanner size={18} />
                  {isSubmitting ? "GENERATING QR..." : "COMPLETE TRANSACTION"}
                </button>
              </div>
            </form>
          )}

          {/* ========================================================
              STEP 2: Active QR Code View (Waiting for customer scan)
              ======================================================== */}
          {step === "qr" && distributionData && (
            <div className="rtcd-qr-section">
              {/* Radar scanner pulse indicator */}
              <div className="rtcd-scanner-status">
                <div className="rtcd-pulse-dot" />
                <span className="rtcd-scanner-text">
                  Waiting for customer to scan QR code...
                </span>
              </div>

              {/* Generated QR Code Canvas */}
              <div className="rtcd-qr-frame">
                <div className="rtcd-qr-canvas-wrapper">
                  <QRCodeCanvas
                    value={distributionData.qr_data || distributionData.token}
                    size={220}
                    level="H"
                    includeMargin={true}
                  />
                </div>
              </div>

              {/* Transaction Summary Card */}
              <div className="rtcd-qr-details">
                <div className="rtcd-qr-details-title">Transaction Details</div>
                <div className="rtcd-qr-detail-row">
                  <span>Invoice Number:</span>
                  <strong>{distributionData.invoice_no}</strong>
                </div>
                <div className="rtcd-qr-detail-row">
                  <span>Transaction Amount:</span>
                  <strong>£{Number(distributionData.transaction_total).toFixed(2)}</strong>
                </div>
                <div className="rtcd-qr-detail-row">
                  <span>Awarding TopCoin:</span>
                  <strong className="rtcd-highlight-reward">
                    {distributionData.awarding_topcoin} TC / {distributionData.awarding_mtopcoin} mTC
                  </strong>
                </div>
                <div className="rtcd-qr-detail-row">
                  <span>Cashier / Employee:</span>
                  <strong>{distributionData.employee_name} ({distributionData.employee_no})</strong>
                </div>
                <div className="rtcd-qr-detail-row">
                  <span>Type:</span>
                  <strong>{distributionData.transaction_type}</strong>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="rtcd-qr-actions">
                {/* <button
                  type="button"
                  className="rtcd-secondary-btn"
                  onClick={copyClaimUrl}
                >
                  <MdContentCopy style={{ verticalAlign: "middle", marginRight: 4 }} />
                  Copy Link
                </button> */}
                <button
                  type="button"
                  className="rtcd-cancel-btn"
                  onClick={handleCancelSession}
                >
                  <MdClose style={{ verticalAlign: "middle", marginRight: 4 }} />
                  Cancel Transaction
                </button>
              </div>

              {/* Testing / Simulation Box for instant verification */}
              {/* <div className="rtcd-dev-box">
                <p>Test Scan Simulation (Simulate Customer Scanning):</p>
                <div className="rtcd-dev-input-group">
                  <input
                    type="text"
                    className="rtcd-dev-input"
                    placeholder="Customer ID or Email (optional, e.g. 2 or customer@example.com)"
                    value={testCustomerId}
                    onChange={(e) => setTestCustomerId(e.target.value)}
                  />
                  <button
                    type="button"
                    className="rtcd-dev-btn"
                    disabled={isSimulatingClaim}
                    onClick={handleSimulateClaim}
                  >
                    {isSimulatingClaim ? "Claiming..." : "Simulate Customer Scan"}
                  </button>
                </div>
              </div> */}
            </div>
          )}

          {/* ========================================================
              STEP 3: Completed Receipt View
              ======================================================== */}
          {step === "completed" && completedTxData && (
            <div className="rtcd-success-receipt">
              <MdCheckCircle className="rtcd-success-icon" />
              <h4 className="rtcd-success-title">TRANSACTION COMPLETED!</h4>
              <p className="rtcd-success-subtitle">
                Customer scanned the QR code and received TopCoins.
              </p>

              <div className="rtcd-success-details">
                <div className="rtcd-qr-detail-row">
                  <span>SBC Reference:</span>
                  <strong>{completedTxData.sbc_ref || "SBC-CONFIRMED"}</strong>
                </div>
                <div className="rtcd-qr-detail-row">
                  <span>Customer:</span>
                  <strong>
                    {completedTxData.customer?.name || "Customer Verified"}
                    {completedTxData.customer?.id ? ` (${completedTxData.customer.id})` : ""}
                  </strong>
                </div>
                <div className="rtcd-qr-detail-row">
                  <span>Total Amount:</span>
                  <strong>£{Number(completedTxData.transaction_total).toFixed(2)}</strong>
                </div>
                <div className="rtcd-qr-detail-row">
                  <span>Awarded TopCoin:</span>
                  <strong className="rtcd-highlight-reward">
                    {completedTxData.awarding_topcoin} TopCoin / {completedTxData.awarding_mtopcoin} M-TopCoin
                  </strong>
                </div>
                <div className="rtcd-qr-detail-row">
                  <span>Invoice No:</span>
                  <strong>{completedTxData.invoice_no}</strong>
                </div>
                <div className="rtcd-qr-detail-row">
                  <span>Cashier:</span>
                  <strong>{completedTxData.employee_name} ({completedTxData.employee_no})</strong>
                </div>
                {completedTxData.completed_at && (
                  <div className="rtcd-qr-detail-row">
                    <span>Completed At:</span>
                    <strong>{new Date(completedTxData.completed_at).toLocaleString("en-GB")}</strong>
                  </div>
                )}
              </div>

              <button
                type="button"
                className="rtcd-reset-btn"
                onClick={handleNewTransaction}
              >
                <MdRefresh style={{ verticalAlign: "middle", marginRight: 6 }} />
                START NEW TRANSACTION
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default RetailTcDistribution;
